import React, { useRef, useEffect, useState, Suspense, lazy } from "react";
import "./App.css";
import Testimonials from "./components/Testimonials";

// Lazy-loaded components
const JobDetail = lazy(() => import("./components/JobDetailComponent"));
const CareerAdvice = lazy(() => import("./components/CareerAdviceComponent"));
const SuccessStories = lazy(() => import("./components/SuccessStoriesComponent"));

// Loading Spinner
function LoadingSpinner() {
  return (
    <div className="loading-screen">
      <div className="spinner"></div>
      <p>Loading opportunities...</p>
    </div>
  );
}

// Contact Form
function ContactForm() {
  return (
    <section className="contact-section">
      <form
        action="https://formsubmit.co/baranisp304@gmail.com"
        method="POST"
        className="contact-form"
        autoComplete="off"
      >
        <h2>Contact Us</h2>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">
              Name<span>*</span>
            </label>
            <input
              type="text"
              id="firstName"
              name="name"
              placeholder="Your Name"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">
              Email<span>*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="you@example.com"
              required
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="message">
            Message<span>*</span>
          </label>
          <textarea id="message" name="message" rows="4" required></textarea>
        </div>
        <input
          type="hidden"
          name="_next"
          value="https://yourwebsite.com/thankyou.html"
        />
        <input type="hidden" name="_captcha" value="false" />
        <div className="form-actions">
          <button type="submit" className="btn-submit">
            SUBMIT
          </button>
        </div>
      </form>
    </section>
  );
}

// MAIN APP
export default function App() {
  const jobRef = useRef(null);
  const contactRef = useRef(null);

  const [fadeIn, setFadeIn] = useState(false);
  const [page, setPage] = useState("home");
  const [selectedJob, setSelectedJob] = useState(null);

  const [jobs, setJobs] = useState([]);
  const [visibleJobs, setVisibleJobs] = useState(6);
  const [loadingJobs, setLoadingJobs] = useState(true);

  // Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredJobs, setFilteredJobs] = useState([]);

  // Fetch jobs from Remotive API
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoadingJobs(true);
        const response = await fetch("https://remotive.com/api/remote-jobs");
        const data = await response.json();
        setJobs(data.jobs || []);
        setFilteredJobs(data.jobs || []);
      } catch (err) {
        console.error("Error fetching jobs:", err);
      } finally {
        setLoadingJobs(false);
      }
    };
    fetchJobs();
  }, []);

  // Search handler
  const handleSearch = () => {
    const filtered = jobs.filter(
      (job) =>
        job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.company_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredJobs(filtered);
  };

  // Scroll fade-in
  useEffect(() => {
    const handleScroll = () => {
      if (jobRef.current) {
        const top = jobRef.current.getBoundingClientRect().top;
        if (top < window.innerHeight - 100) setFadeIn(true);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setPage("jobDetail");
  };

  const handleShowMore = () => {
    setVisibleJobs((prev) => prev + 6);
  };

  const renderPage = () => {
    if (page === "jobDetail" && selectedJob) {
      return (
        <Suspense fallback={<LoadingSpinner />}>
          <JobDetail
            selectedJob={selectedJob}
            goHome={() => setPage("home")}
            goAdvice={() => setPage("careerAdvice")}
          />
        </Suspense>
      );
    }

    if (page === "careerAdvice") {
      return (
        <Suspense fallback={<LoadingSpinner />}>
          <CareerAdvice goHome={() => setPage("home")} />
        </Suspense>
      );
    }

    if (page === "successStories") {
      return (
        <Suspense fallback={<LoadingSpinner />}>
          <SuccessStories goHome={() => setPage("home")} />
        </Suspense>
      );
    }

    // HOME PAGE
    return (
      <>
        <section className="hero job-hero">
          <div className="overlay">
            <h1 className="hero-title">Find Your Dream Job</h1>
            <p className="hero-subtitle">
              Explore top career opportunities from leading companies.
            </p>
            <button
              className="hero-btn"
              onClick={() =>
                jobRef.current.scrollIntoView({ behavior: "smooth" })
              }
            >
              Browse Jobs
            </button>
          </div>
        </section>

        <section
          className={`destinations ${fadeIn ? "fade-in" : ""}`}
          ref={jobRef}
        >
          {/* Header with search */}
          <div className="section-header">
            <h2 className="section-title">Featured Jobs</h2>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search by title or company..."
                className="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button className="search-btn" onClick={handleSearch}>
                Search
              </button>
            </div>
          </div>

          <div className="destination-grid">
            {loadingJobs ? (
              <LoadingSpinner />
            ) : filteredJobs.length === 0 ? (
              <p>No jobs found.</p>
            ) : (
              filteredJobs.slice(0, visibleJobs).map((job, index) => {
                const imgSrc =
                  job.company_logo && job.company_logo !== ""
                    ? job.company_logo
                    : `https://source.unsplash.com/400x300/?office,job,career&sig=${index}`;

                return (
                  <div
                    key={index}
                    className="destination-card"
                    onClick={() =>
                      handleJobClick({
                        title: job.title,
                        company_name: job.company_name,
                        image: imgSrc,
                        salary: job.salary || "Not specified",
                        description:
                          job.description || "No description available.",
                        url: job.url,
                      })
                    }
                  >
                    <img src={imgSrc} alt={job.title} className="job-image" />
                    <div className="destination-info">
                      <h3>{job.title}</h3>
                      <p>{job.company_name}</p>
                      <p className="price">{job.salary || "Not specified"}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {!loadingJobs && filteredJobs.length > visibleJobs && (
            <>
              <div className="show-more-container">
                <button className="show-more-btn" onClick={handleShowMore}>
                  Load more
                </button>
              </div>
              <hr className="separator-line" />
            </>
          )}

          <Testimonials />
        </section>
      </>
    );
  };


  return (
    <div className="app">
      <header className="header">
        <h2>JobFinder</h2>
        <nav>
          <button onClick={() => setPage("home")}>Home</button>
          <button onClick={() => setPage("careerAdvice")}>Career Advice</button>
          <button onClick={() => setPage("successStories")}>Success Stories</button>
          <button
            onClick={() =>
              contactRef.current.scrollIntoView({ behavior: "smooth" })
            }
          >
            Contact
          </button>
        </nav>
      </header>

      {renderPage()}

      <div ref={contactRef}>
        <ContactForm />
      </div>

      <footer>
        <p>© 2025 JobFinder</p>
        <p style={{ display: "flex", justifyContent: "right", marginLeft: "auto" }}>Designed by Baraninathan SP</p>
      </footer>
    </div>
  );
}
